import { Indicator } from '../../models/indicator';

/**
 * Defines what can be picked in the indicator pick list.
 * - `sets`: a set can be picked - always as a whole (the set and all of its children)
 * - `children`: children of a set can be picked on their own
 */
export interface SetSelectionRule {
  sets: boolean;
  children: boolean;
}

/** Only whole sets, e.g. for exports. */
export const WHOLE_SETS_RULE: SetSelectionRule = {
  sets: true,
  children: false,
};

export interface SetMoveContext {
  /** The current selection. */
  selection: Indicator[];
  /** All indicators by id (fresh store items). */
  items: Record<string, Indicator>;
  /** Children by set id, independent of any filter. */
  children: Record<string, Indicator[]>;
  rule: SetSelectionRule;
}

export interface SetMoveOptions {
  /** The move comes from "move all". */
  moveAll?: boolean;
  /** Ids matching the current filter - sets outside are only shown as context. */
  matchingIds?: Set<string>;
}

/**
 * Applies a pick-list move to the selection according to the set rule.
 *
 * Moving to the target:
 * - a set brings all of its children (the set itself only if `rule.sets`)
 * - a child is added on its own if `rule.children`, otherwise its whole set is added
 * - on "move all", context sets (not matching the filter) don't bring their hidden
 *   children if single children are allowed
 *
 * Moving to the source:
 * - a set is removed with all of its children
 * - a child of a selected set breaks the set up if `rule.children` (the set is
 *   removed, the siblings stay), otherwise the whole set is removed
 *
 * Afterwards the selection is normalized, see {@link normalizeSetSelection}.
 *
 * @param {SetMoveContext} context - Selection, store items, children and rule.
 * @param {Indicator[]} moved - The moved indicators.
 * @param {'add' | 'remove'} direction - Whether the items were moved to the target or back.
 * @param {SetMoveOptions} [options] - Additional information about the move.
 * @return {Indicator[]} The new selection.
 */
export function applySetMove(
  context: SetMoveContext,
  moved: Indicator[],
  direction: 'add' | 'remove',
  options: SetMoveOptions = {}
): Indicator[] {
  const { rule, children, items } = context;
  const ids = new Set(context.selection.map(i => i._id));
  const childIds = (setId: string) => (children[setId] || []).map(c => c._id);
  const wholeSet = (setId: string) => [setId, ...childIds(setId)];

  for (const indicator of moved) {
    if (direction === 'add') {
      if (indicator.isSet) {
        const isContext =
          options.moveAll &&
          options.matchingIds &&
          !options.matchingIds.has(indicator._id);
        // the matching children are part of `moved` themselves
        if (isContext && rule.children) continue;
        wholeSet(indicator._id).forEach(id => ids.add(id));
      } else if (
        indicator.parent &&
        items[indicator.parent] &&
        !rule.children
      ) {
        wholeSet(indicator.parent).forEach(id => ids.add(id));
      } else {
        ids.add(indicator._id);
      }
    } else if (indicator.isSet) {
      wholeSet(indicator._id).forEach(id => ids.delete(id));
    } else if (indicator.parent && ids.has(indicator.parent)) {
      if (rule.children) {
        ids.delete(indicator.parent);
        ids.delete(indicator._id);
      } else {
        wholeSet(indicator.parent).forEach(id => ids.delete(id));
      }
    } else {
      ids.delete(indicator._id);
    }
  }

  return normalizeSetSelection({
    ...context,
    selection: toIndicators(ids, items),
  });
}

/**
 * Enforces the set rule on a selection:
 * - a selected set always comes with all of its children
 * - sets are dropped if `rule.sets` is false (their children stay)
 * - without `rule.children`, a child whose set isn't selected pulls in its whole set
 *
 * @param {SetMoveContext} context - Selection, store items, children and rule.
 * @return {Indicator[]} The normalized selection (fresh store items, stable order).
 */
export function normalizeSetSelection(context: SetMoveContext): Indicator[] {
  const { rule, children, items } = context;
  const ids = new Set(context.selection.map(i => i._id));

  for (const id of [...ids]) {
    const indicator = items[id];
    if (!indicator) {
      ids.delete(id);
      continue;
    }
    if (
      !rule.children &&
      rule.sets &&
      indicator.parent &&
      items[indicator.parent] &&
      !ids.has(indicator.parent)
    ) {
      ids.add(indicator.parent);
    }
  }

  for (const id of [...ids]) {
    if (!items[id]?.isSet) continue;
    (children[id] || []).forEach(child => ids.add(child._id));
    if (!rule.sets) ids.delete(id);
  }

  return toIndicators(ids, items);
}

const toIndicators = (
  ids: Set<string>,
  items: Record<string, Indicator>
): Indicator[] => [...ids].map(id => items[id]).filter(item => !!item);
